import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import confetti from 'canvas-confetti';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

interface CoquinRouletteProps {
  players: string[];
  defiPhysique: string[];
  questionIntime: string[];
  onBack: () => void;
}

const SVG_SIZE = 300;
const CENTER = SVG_SIZE / 2;
const RADIUS = 135;

const SEGMENTS = [
  { name: 'Question', name2: 'Intime', color: '#F472B6', icon: '💋', type: 'intime' },
  { name: 'Défi', name2: 'Physique', color: '#EF4444', icon: '💪', type: 'defi' },
  { name: 'Question', name2: 'Intime', color: '#F472B6', icon: '💋', type: 'intime' },
  { name: 'Défi', name2: 'Physique', color: '#EF4444', icon: '💪', type: 'defi' },
  { name: 'Question', name2: 'Intime', color: '#F472B6', icon: '💋', type: 'intime' },
  { name: 'Défi', name2: 'Physique', color: '#EF4444', icon: '💪', type: 'defi' },
];

const SLOT_DURATION = 1200;
const SLOT_TICKS = 16;

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = (angleDeg - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y} Z`;
}

export default function CoquinRoulette({ players, defiPhysique, questionIntime, onBack }: CoquinRouletteProps) {
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotationDeg, setRotationDeg] = useState(0);
  const [winner, setWinner] = useState<typeof SEGMENTS[0] | null>(null);
  const [resultText, setResultText] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [showNextBtn, setShowNextBtn] = useState(false);
  const animationRef = useRef<number>(0);
  const lastSegRef = useRef(-1);
  const svgRef = useRef<SVGSVGElement>(null);
  const lastQRef = useRef('');

  // Slot machine
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [isSlotSpinning, setIsSlotSpinning] = useState(true);
  const [slotDisplayName, setSlotDisplayName] = useState('');
  const slotTimers = useRef<number[]>([]);
  const turnCount = useRef(0);

  const shuffledOrder = useMemo(() => {
    const indices = players.map((_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    return indices;
  }, [players]);

  const currentPlayer = players[shuffledOrder[currentPlayerIndex % shuffledOrder.length]] || '';

  const runSlotMachine = useCallback((targetName: string) => {
    setIsSlotSpinning(true);
    setWinner(null);
    setResultText(null);
    setShowResult(false);
    setShowNextBtn(false);

    const interval = SLOT_DURATION / SLOT_TICKS;
    for (let i = 0; i < SLOT_TICKS; i++) {
      const timerId = window.setTimeout(() => {
        if (i < SLOT_TICKS - 1) {
          setSlotDisplayName(players[Math.floor(Math.random() * players.length)]);
        } else {
          setSlotDisplayName(targetName);
          setIsSlotSpinning(false);
          try { Haptics.impact({ style: ImpactStyle.Medium }); } catch {}
        }
      }, interval * i + (i > SLOT_TICKS * 0.7 ? (i - SLOT_TICKS * 0.7) * 30 : 0));
      slotTimers.current.push(timerId);
    }
  }, [players]);

  useEffect(() => {
    const timers = slotTimers.current;
    runSlotMachine(currentPlayer);
    return () => { timers.forEach(id => window.clearTimeout(id)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const segAngle = 360 / SEGMENTS.length;

  const segments = useMemo(() => SEGMENTS.map((seg, i) => {
    const start = i * segAngle;
    const end = start + segAngle;
    const mid = start + segAngle / 2;
    const r = RADIUS * 0.58;
    const pos = polarToCartesian(CENTER, CENTER, r, mid);
    return { ...seg, path: describeArc(CENTER, CENTER, RADIUS, start, end), x: pos.x, y: pos.y };
  }), [segAngle]);

  const updateRotation = useCallback((deg: number) => {
    const svg = svgRef.current;
    if (svg) {
      const g = svg.getElementById('coquin-wheel');
      if (g) g.setAttribute('transform', `rotate(${deg} ${CENTER} ${CENTER})`);
    }
  }, []);

  const spin = useCallback(() => {
    if (isSpinning || isSlotSpinning) return;
    setIsSpinning(true);
    setWinner(null);
    setResultText(null);
    setShowResult(false);
    setShowNextBtn(false);
    lastSegRef.current = -1;

    const total = rotationDeg + 360 * (6 + Math.random() * 6) + Math.random() * 360;
    const duration = 3000 + Math.random() * 2000;
    const startTime = performance.now();
    const startRot = rotationDeg;
    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

    const animate = (time: number) => {
      const progress = Math.min((time - startTime) / duration, 1);
      const deg = startRot + (total - startRot) * easeOut(progress);
      updateRotation(deg);

      const seg = Math.floor(((deg % 360 + 360) % 360) / segAngle);
      if (seg !== lastSegRef.current && lastSegRef.current !== -1) {
        try { Haptics.impact({ style: ImpactStyle.Light }); } catch {}
      }
      lastSegRef.current = seg;

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setRotationDeg(deg);
        setIsSpinning(false);

        const norm = ((deg % 360) + 360) % 360;
        const idx = Math.floor(((360 - norm) % 360) / segAngle) % SEGMENTS.length;
        const w = SEGMENTS[idx];
        setWinner(w);

        const pool = w.type === 'defi' ? defiPhysique : questionIntime;
        let q = 'Contenu à venir... 🔥';
        if (pool.length > 0) {
          const avail = pool.filter(x => x !== lastQRef.current);
          const p = avail.length > 0 ? avail : pool;
          q = p[Math.floor(Math.random() * p.length)];
          lastQRef.current = q;
        }

        // Replace [Prénom] with current player, [Prénom2] with random other
        const currentName = players[shuffledOrder[currentPlayerIndex % shuffledOrder.length]] || '';
        const others = players.filter(p2 => p2 !== currentName);
        const prenom2 = others.length > 0 ? others[Math.floor(Math.random() * others.length)] : currentName;
        q = q.replace(/\[Prénom2\]/g, prenom2).replace(/\[Prénom\]/g, currentName);

        setResultText(q);

        try { Haptics.impact({ style: ImpactStyle.Heavy }); } catch {}
        confetti({ particleCount: 50, spread: 60, origin: { y: 0.5 }, colors: ['#EF4444', '#F472B6', '#FCA5A5'] });
        setTimeout(() => setShowResult(true), 400);
        setTimeout(() => setShowNextBtn(true), 1200);
      }
    };
    animationRef.current = requestAnimationFrame(animate);
  }, [isSpinning, isSlotSpinning, rotationDeg, segAngle, updateRotation, defiPhysique, questionIntime, currentPlayerIndex, players, shuffledOrder]);

  useEffect(() => {
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, []);

  const handleNextTurn = () => {
    setShowResult(false);
    setShowNextBtn(false);
    setWinner(null);
    setResultText(null);
    turnCount.current += 1;
    const nextIndex = (currentPlayerIndex + 1) % players.length;
    setCurrentPlayerIndex(nextIndex);
    const nextPlayer = players[shuffledOrder[nextIndex % shuffledOrder.length]] || '';
    runSlotMachine(nextPlayer);
  };

  const canSpin = !isSpinning && !isSlotSpinning;

  return (
    <div className="h-screen bg-gradient-to-b from-[#450a0a] via-[#5c1010] to-[#2a0505] flex flex-col items-center safe-top safe-bottom relative overflow-hidden">
      <button onClick={onBack} className="absolute left-6 z-20 w-10 h-10 rounded-full bg-black/30 backdrop-blur-md flex items-center justify-center text-white/60 btn-press border border-white/10" style={{ top: 'max(env(safe-area-inset-top, 0px) + 8px, 24px)' }}>←</button>

      {/* Turn counter */}
      <div className="absolute right-6 z-10" style={{ top: 'max(env(safe-area-inset-top, 0px) + 8px, 24px)' }}>
        <span className="text-white/30 font-body text-sm">Tour {turnCount.current + 1}</span>
      </div>

      {/* Player name slot machine */}
      <div className="flex-shrink-0 pt-14 pb-2 px-6 text-center z-10">
        <p className="text-red-300/50 font-body text-xs mb-1">C'est au tour de...</p>
        <div className="h-12 flex items-center justify-center overflow-hidden">
          <h1 key={slotDisplayName} className={`font-display font-black text-3xl text-white drop-shadow-lg ${isSlotSpinning ? 'slot-tick' : 'slot-land'}`}>{slotDisplayName}</h1>
        </div>
      </div>

      {/* Wheel */}
      {!showResult && (
        <div className="flex-1 flex flex-col items-center justify-center z-10">
          <div className="mb-[-10px] z-20 flex-shrink-0">
            <div className="debat-indicator" style={{ width: 0, height: 0, borderLeft: '12px solid transparent', borderRight: '12px solid transparent', borderTop: '22px solid #FFD700', filter: 'drop-shadow(0 0 6px rgba(255,215,0,0.5))' }} />
          </div>
          <div className={`flex-shrink-0 ${canSpin ? 'cursor-pointer' : ''}`} onClick={canSpin ? spin : undefined} style={{ width: '70vw', maxWidth: '340px', aspectRatio: '1' }}>
            <svg ref={svgRef} viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`} width="100%" height="100%">
              <circle cx={CENTER} cy={CENTER} r={RADIUS + 4} fill="none" stroke="rgba(239,68,68,0.3)" strokeWidth="3" />
              <g id="coquin-wheel" transform={`rotate(${rotationDeg} ${CENTER} ${CENTER})`}>
                {segments.map((seg, i) => (
                  <g key={i}>
                    <path d={seg.path} fill={seg.color} stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
                    <text x={seg.x} y={seg.y - 12} textAnchor="middle" dominantBaseline="central" fontSize="20">{seg.icon}</text>
                    <text x={seg.x} y={seg.y + 6} textAnchor="middle" dominantBaseline="central" fill="white" fontSize="8" fontWeight="bold" fontFamily="Poppins, sans-serif">{seg.name}</text>
                    <text x={seg.x} y={seg.y + 17} textAnchor="middle" dominantBaseline="central" fill="white" fontSize="8" fontWeight="bold" fontFamily="Poppins, sans-serif">{seg.name2}</text>
                  </g>
                ))}
              </g>
              <circle cx={CENTER} cy={CENTER} r={22} fill="#450a0a" stroke="rgba(239,68,68,0.4)" strokeWidth="2.5" />
              <text x={CENTER} y={CENTER + 1} textAnchor="middle" dominantBaseline="central" fontSize="18">🌶️</text>
            </svg>
          </div>
          <div className="flex-shrink-0 mt-6" style={{ height: '56px' }}>
            {canSpin && !winner && (
              <button onClick={spin} className="px-10 py-4 rounded-2xl bg-gradient-to-r from-red-500 to-pink-500 text-white font-display font-bold text-base btn-press shadow-[0_4px_20px_rgba(239,68,68,0.4)] animate-pulse-glow">Tourner 🎡</button>
            )}
          </div>
        </div>
      )}

      {/* Result */}
      {showResult && winner && resultText && (
        <div className="flex-1 flex flex-col items-center justify-center px-6 z-10 animate-fade-in">
          <div className="mb-5 text-center debat-zoom-in">
            <span className="text-5xl mb-2 block">{winner.icon}</span>
            <h2 className="font-display font-black text-2xl drop-shadow-lg" style={{ color: winner.color }}>{winner.name} {winner.name2}</h2>
          </div>
          <div className="w-full max-w-sm debat-card-spring">
            <div className="rounded-3xl p-6 border" style={{ background: `linear-gradient(135deg, ${winner.color} 0%, ${winner.color}99 100%)`, borderColor: `${winner.color}55`, boxShadow: `0 0 30px ${winner.color}33` }}>
              <p className="text-white font-display font-bold text-center leading-relaxed flex items-center justify-center" style={{ fontSize: resultText.length > 80 ? '16px' : '20px', minHeight: '80px' }}>{resultText}</p>
            </div>
          </div>
          {showNextBtn && (
            <button onClick={handleNextTurn} className="mt-6 w-full max-w-sm py-4 rounded-2xl bg-gradient-to-r from-red-500 to-pink-500 text-white font-display font-bold text-base btn-press animate-fade-in shadow-[0_4px_20px_rgba(239,68,68,0.3)]">Tour suivant →</button>
          )}
        </div>
      )}
    </div>
  );
}
